import { Component,
  EventEmitter,
  Input,
  Output,
  forwardRef,
  ElementRef,
  ViewChild,
  Renderer,
  AfterContentInit,
  ContentChildren,
  QueryList,
  Optional,
  OnInit, ViewEncapsulation
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { coerceBooleanProperty, arrayLikeEqual } from '../common/shared';

let nextId = 0;

export class PuiCheckboxChange {
  source: PuiCheckboxComponent | PuiCheckboxGroupComponent;
  value?: any;
};

export const PUI_CHECKBOX_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => PuiCheckboxComponent),
  multi: true
};

export const PUI_CHECKBOX_GROUP_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => PuiCheckboxGroupComponent),
  multi: true
};

@Component({
  exportAs: 'puiInputGroup',
  selector: 'pui-checkbox-group',
  providers: [PUI_CHECKBOX_GROUP_CONTROL_VALUE_ACCESSOR],
  styleUrls: ['checkbox.component.scss'],
  encapsulation: ViewEncapsulation.None,
  template: `
    <div [ngClass]="getClasses()">
      <label class="control-label">{{label}}</label>
      <ng-content></ng-content>
    </div>
  `
})
export class PuiCheckboxGroupComponent implements AfterContentInit, ControlValueAccessor {
  @Input() inline: boolean;
  @Input() label: string;
  @Output() change: EventEmitter<PuiCheckboxChange> = new EventEmitter<PuiCheckboxChange>();

  @ContentChildren(forwardRef(() => PuiCheckboxComponent))
  _checkboxes: QueryList<PuiCheckboxComponent>;

  private delimitor = ',';
  private _value: any = [];
  private _name = `pui-checkbox-group-${nextId++}`;
  private _disabled: boolean;
  private _selected: Array<PuiCheckboxComponent> = [];
  private _isInitialized = false;

  onTouchedCallback: () => void = () => { };
  onChangeCallback: (_: any) => void = () => { };

  getClasses() {
    return {
      'form-group': true,
      'mt-checkbox-list': !this.inline,
      'mt-checkbox-inline': this.inline
    };
  }

  @Input()
  get name(): string {
    return this._name;
  }
  set name(val: string) {
    this._name = val;
    this._updateCheckboxNames();
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(val) {
    this._disabled = coerceBooleanProperty(val);
  }

  @Input()
  get value(): any {
    return this._value;
  }
  set value(val: any) {
    if (!arrayLikeEqual(this._value, val)) {
      this._value = val instanceof Array ? val : (val ? val.split(this.delimitor) : []);
      this._updateSelectedCheckboxFromValue();
      this._checkSelectedCheckbox();
    }
  }

  @Input()
  get selected() {
    return this._selected;
  }
  set selected(val: PuiCheckboxComponent[]) {
    this._selected = val;
    this._value = this._selected.map(box => box.value);
    this._checkSelectedCheckbox();
  }
  addSelected(selected: PuiCheckboxComponent) {
    this._selected.push(selected);
    this._value = this._selected.map(box => box.value);
  }
  removeSelected(checkbox: PuiCheckboxComponent) {
    this._selected.splice(this._selected.indexOf(checkbox), 1);
    this._value = this._selected.map(box => box.value);
  }

  _checkSelectedCheckbox() {
    this.selected.forEach(checkbox => {
      if (!checkbox.checked) {
        checkbox.checked = true;
      }
    });
  }

  ngAfterContentInit() {
    this._isInitialized = true;
  }

  private _updateCheckboxNames(): void {
    if (this._checkboxes) {
      this._checkboxes.forEach(checkbox => {
        checkbox.name = this.name;
      });
    }
  }

  private _updateSelectedCheckboxFromValue(): void {
    let isAlreadySelected = this._selected && arrayLikeEqual(this._selected.map(box => box.value), this._value);
    if (this._checkboxes && !isAlreadySelected) {
      this._selected = [];
      this._checkboxes.forEach(checkbox => {
        checkbox.checked = this.value.indexOf(checkbox.value) >= 0;
        if (checkbox.checked) {
          this._selected.push(checkbox);
        }
      });
    }
  }

  _emitChangeEvent(): void {
    if (this._isInitialized) {
      let e = <PuiCheckboxChange>{
        source: this,
        value: this.value
      };
      this.change.emit(e);
    }
  }

  writeValue(val: any) {
    this.value = val;
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }
}

@Component({
  exportAs: 'puiInput',
  selector: 'pui-checkbox',
  providers: [PUI_CHECKBOX_CONTROL_VALUE_ACCESSOR],
  templateUrl: 'checkbox.component.html'
})
export class PuiCheckboxComponent implements OnInit, ControlValueAccessor {
  @Input() id = `pui-checkbox-${nextId++}`;
  @Input() name: string;
  @Input() label: string;

  private _value: any;
  @Input()
  get value(): any {
    return this._value;
  }
  set value(val: any) {
    if (this._value !== val) {
      this._value = val;
      if (this.checkboxGroup) {
        this.checked = this.checkboxGroup.value && this.checkboxGroup.value.indexOf(val) >= 0;
      }
    }
  }

  /**
   * e.g user must accept some terms before submitting.
   */
  private _required: boolean;
  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(val) {
    this._required = coerceBooleanProperty(val);
  }

  private _disabled: boolean;
  @Input()
  get disabled() {
    return this._disabled || (this.checkboxGroup && this.checkboxGroup.disabled);
  }
  set disabled(val) {
    this._disabled = coerceBooleanProperty(val);
  }

  private _checked: boolean;
  @Input()
  get checked(): boolean {
    return this._checked;
  }
  set checked(checked) {
    if (this.checked !== checked) {
      this._checked = coerceBooleanProperty(checked);
      if (checked && this.checkboxGroup && this.checkboxGroup.value.indexOf(this.value) < 0) {
        this.checkboxGroup.addSelected(this);
      } else if (!checked && this.checkboxGroup && this.checkboxGroup.value.indexOf(this.value) >= 0) {
        this.checkboxGroup.removeSelected(this);
      }
    }
  }

  @Output() change: EventEmitter<PuiCheckboxChange> = new EventEmitter<PuiCheckboxChange>();

  @ViewChild('input') _inputElement: ElementRef;

  private checkboxGroup: PuiCheckboxGroupComponent;

  constructor(@Optional() checkboxGroup: PuiCheckboxGroupComponent, private _elementRef: ElementRef, private _renderer: Renderer) {
    this.checkboxGroup = checkboxGroup;
  }

  private onTouchedCallback: () => void = () => { };
  private onChangeCallback: (_: any) => void = () => { };

  ngOnInit() {
    if (this.checkboxGroup) {
      this.checked = this.checkboxGroup.value && this.checkboxGroup.value.indexOf(this.value) >= 0;
      this.name = this.checkboxGroup.name;
    }
  }

  writeValue(val: any) {
    this.checked = !!val;
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  getClasses() {
    return {
      'mt-checkbox': true,
      'mt-checkbox-outline': true,
      'mt-checkbox-disabled': this.disabled
    };
  }

  private _emitChangeEvent() {
    let val = this.value ? this.value : this.checked;
    this.onChangeCallback(val);
    this.change.emit(<PuiCheckboxChange>{
      source: this,
      value: val
    });
  }

  onBlur() {
    this.onTouchedCallback();
  }

  onClick(e: Event) {
    e.stopPropagation();
  }

  onChange(e: Event) {
    e.stopPropagation();
    if (!this.disabled) {
      this.checked = !this._checked;
      this._emitChangeEvent();
    }
    if (this.checkboxGroup) {
      this.checkboxGroup.onChangeCallback(this.checkboxGroup.value);
      this.checkboxGroup.onTouchedCallback();
      this.checkboxGroup._emitChangeEvent();
    }
  }
}
